import { apiCareerSite } from "@/api/recruit/career-site.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import { ApiCareerSitePayload } from "@/types/recruit/career-site.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const careerSiteKeys = createQueryKeys("careerSite", {
  details: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id === undefined) throw new ApiPassException("Career site id is required");
      const result = await apiCareerSite.getById(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  getAll: () => ({
    queryKey: ["all"],
    queryFn: async () => {
      const result = await apiCareerSite.getAll();
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useCareerSiteDetails = (id: number | undefined, enabled: boolean = true) => {
  return useQuery({
    ...careerSiteKeys.details(id),
    enabled,
    staleTime: 60 * 1000,
  });
};

export const useCareerSiteGetAll = (enabled: boolean = true) => {
  return useQuery({
    ...careerSiteKeys.getAll(),
    enabled,
    staleTime: 60 * 1000,
  });
};

interface SaveParams {
  id?: number;
  payload: ApiCareerSitePayload;
}

export const useCareerSiteSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function ({ id, payload }: SaveParams) {
      const result = !id
        ? await apiCareerSite.create(payload)
        : await apiCareerSite.update(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (updatedCareerSite) => {
      queryClient.invalidateQueries({ queryKey: careerSiteKeys.getAll._def });
      queryClient.setQueryData(
        careerSiteKeys.details(updatedCareerSite.id).queryKey,
        updatedCareerSite
      );
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useCareerSiteDelete = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async function (id: number) {
      const result = await apiCareerSite.delete(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: careerSiteKeys.getAll._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};
